/**
 * @file seoHelmet.component.tsx
 * @path apps/website/src/components/common/seoHelmet.component.tsx
 * @description Centralized SEO manager using React Helmet.
 * Orchestrates metadata and JSON-LD schemas based on page context.
 */

import React from "react";
import { Helmet } from "react-helmet-async";
import { siteMetadata, branding } from "@/config/site.config";
import {
  generateSEO,
  generateLocalBusinessSchema,
  generateServiceSchemas,
  generateActivitySchemas,
  generateVacancySchemas,
  generateSingleServiceSchema,
  generateSingleActivitySchema,
} from "@/config"; // Assuming these exist in your SEO service/config
import { SEOProps as BaseSEOProps } from "@sirbastians/types";

export interface SEOProps extends BaseSEOProps {
  pageType?:
    | "default"
    | "careers"
    | "serviceList"
    | "activityList"
    | "serviceDetail"
    | "activityDetail";
  slug?: string;
  jsonLd?: Record<string, unknown>[];
}

const SEOHelmet: React.FC<SEOProps> = ({
  pageType = "default",
  slug,
  jsonLd,
  ...props
}) => {
  // ✨ ENHANCED: Pass siteMetadata as base defaults to your generator
  const metadata = generateSEO({
    title: props.title || siteMetadata.title,
    description: props.description || siteMetadata.description,
    image: props.image || branding.ogImage,
    url: props.url || siteMetadata.url,
    ...props,
  });

  const schemas: unknown[] = [];

  // Logic-based Schema Aggregation
  switch (pageType) {
    case "careers":
      schemas.push(generateVacancySchemas());
      break;
    case "serviceList":
      schemas.push(generateLocalBusinessSchema());
      schemas.push(generateServiceSchemas());
      break;
    case "activityList":
      schemas.push(generateLocalBusinessSchema());
      schemas.push(generateActivitySchemas());
      break;
    case "serviceDetail":
      if (slug) schemas.push(generateSingleServiceSchema(slug));
      break;
    case "activityDetail":
      if (slug) schemas.push(generateSingleActivitySchema(slug));
      break;
    default:
      schemas.push(generateLocalBusinessSchema());
      schemas.push(generateServiceSchemas());
      schemas.push(generateActivitySchemas());
      break;
  }

  // Merge external schemas
  if (jsonLd && Array.isArray(jsonLd)) {
    schemas.push(...jsonLd);
  }

  const validSchemas = schemas.filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{metadata.title}</title>
      <meta name="description" content={metadata.description} />
      {metadata.keywords && (
        <meta name="keywords" content={metadata.keywords} />
      )}
      <meta name="author" content={siteMetadata.title} />
      <link rel="canonical" href={metadata.canonical} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={metadata.og.type || "website"} />
      <meta property="og:url" content={metadata.og.url} />
      <meta property="og:title" content={metadata.og.title} />
      <meta property="og:description" content={metadata.og.description} />
      <meta property="og:image" content={metadata.og.image} />
      <meta property="og:site_name" content={siteMetadata.title} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={metadata.twitter.title} />
      <meta name="twitter:description" content={metadata.twitter.description} />
      <meta name="twitter:image" content={metadata.twitter.image} />

      {/* Structured Data (JSON-LD) */}
      {validSchemas.map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEOHelmet;
